import { Component, ElementRef, Inject, OnInit, Renderer2 } from '@angular/core';
import { Validators, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { AddEditFirmStaffComponent } from 'app/components/firm/add-edit/add-edit-firm-staff/add-edit-firm-staff.component';
import { IPerson } from 'app/models/People/person';
import { IPersonFilter } from 'app/models/filters/personFilter';
import { IFirmApplication } from 'app/models/firms/firm-application';
import { IFirmPerson } from 'app/models/firms/firmPerson';
import { FirmService } from 'app/services/firm/firm-service';
import { PersonService } from 'app/services/person/person.service';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-add-firm-application-person',
  templateUrl: './add-firm-application-person.component.html',
  styleUrls: ['./add-firm-application-person.component.scss']
})
export class AddFirmApplicationPersonComponent implements OnInit {
  public staffList: IPerson[] = [];
  public filteredStaffList: IPerson[] = [];
  public selectedStaffMember: IPerson;
  public firmPersonToAdd: IFirmPerson = {id: 0, isPOC: false, firm: null, person: null, application: null}
  public peopleFilter: IPersonFilter = {filterAffiliation: [], filterStatus: '', filterString: '', expirationStartDate: '', expirationEndDate: '', filterDiscipline: []};
  private readonly debounceString: Subject<string> = new Subject<string>();
  public uiData = LEAD_BASED_PAINT;
  
  public createNew = 'Create a New Person'
  public personDataForm = null;

  constructor(private readonly dialogRef: MatDialogRef<AddEditFirmStaffComponent>,
    public personService: PersonService,
    public firmService: FirmService,
    @Inject(MAT_DIALOG_DATA) public data:{firmApplication:IFirmApplication, person:IFirmPerson, firmPeople:IFirmPerson[]},      
    private readonly toastr: ToastrService,
    private readonly renderer: Renderer2, 
    private readonly el: ElementRef) 
    {
      this.initializeDebounceFunction(); 
      this.setPersonDataFormFields();
    }

  ngOnInit(): void {     
    if(this.data.person !== null && this.data.person !== undefined){
      this.firmPersonToAdd = this.data.person;
      this.peopleFilter.filterString = this.data.person.person.id.toString();
      this.personService.getPeopleFiltered(this.peopleFilter).subscribe(result=>{
        this.staffList = result
        this.filteredStaffList = this.staffList;
        this.selectedStaffMember = this.staffList[0];
      },error=>{this.toastr.error("An error occured getting the firm application staff list: ", error);});
    }
    else{
      this.resetStaffList();     
    }    
    this.firmPersonToAdd.application = this.data.firmApplication;      
  }

  initializeDebounceFunction(): void {
    this.debounceString.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(value => {      
      this.filterStaff(value.trim().toLowerCase());     
    });
  }

  resetStaffList(): void {
    this.staffList = [{id: 0, firstName: this.createNew, lastName: '', middleName: '', title: '', ssn: '', dateOfBirth: '', inactive: false, response: 'Unk', certificates: null}];        
    this.filteredStaffList = this.staffList;
  }

  updateDebounceString(event: any){    
    this.debounceString.next(event.target.value);  
    this.updateScrollContainerHeight();  
  }

  updateScrollContainerHeight() {
    if (this.selectedStaffMember.id === 0) {
      const scrollContainer = this.el.nativeElement.querySelector('.scroll-container');
      this.renderer.setStyle(scrollContainer, 'height', '60vh');
    }
    else {
      const scrollContainer = this.el.nativeElement.querySelector('.scroll-container');
      this.renderer.setStyle(scrollContainer, 'height', '10vh');
    }
  }

  filterStaff(value: any) {    
    if (value.length < 3) {
      this.resetStaffList();
    }
    else{      
      this.peopleFilter.filterString = value;
      this.personService.getPeopleFiltered(this.peopleFilter).subscribe(result=>{
        this.staffList = result.filter(person => !this.data.firmPeople.some(firmPerson => firmPerson.person.id === person.id));
        this.filteredStaffList = this.staffList;
      },error=>{this.toastr.error("An error occured getting the firm application staff list: ", error);});
    }    
  }
  
  displayStaffMember(staffMember: any): string {
    if (staffMember) {
        if (staffMember.id === 0) {
            return staffMember.firstName;
        } else {
            return `${staffMember.id}: ${staffMember.firstName} ${staffMember.lastName}`;
        }
    } else {
        return '';
    }
  }
  
  setPersonDataFormFields(){
    this.personDataForm = new UntypedFormGroup({      
      firstName: new UntypedFormControl("", [Validators.required]),
      lastName: new UntypedFormControl("", [Validators.required]),
      middleName: new UntypedFormControl(""),
      title: new UntypedFormControl(""),
      ssn: new UntypedFormControl("", [Validators.pattern(/^\(?([0-9]{3})\)?-?([0-9]{2})-?([0-9]{4}).*$/)]),
      dateOfBirth: new UntypedFormControl(null),            
    }); 
  }
  
  onCancel(): void {
    this.dialogRef.close(false);
  }
  onSubmit(): void {
    if(this.filteredStaffList.some(person => person.id === this.selectedStaffMember?.id)){   
      if(this.selectedStaffMember.id === 0){
        this.savePersonData();
      }
      else{
        this.saveStaffMember();      
      }                  
    }
    else{
      this.toastr.error("The selected staff member can not be found")
    }
  }

  savePersonData(){
    this.selectedStaffMember = {id: 0, firstName: this.personDataForm.controls.firstName.value, lastName: this.personDataForm.controls.lastName.value, middleName: this.personDataForm.controls.middleName.value, title: this.personDataForm.controls.title.value, ssn: this.personDataForm.controls.ssn.value, dateOfBirth: this.personDataForm.controls.dateOfBirth.value, inactive: false, response: 'Unk',certificates: null};        
    this.personService.savePerson(this.selectedStaffMember).subscribe(result=>{
      this.selectedStaffMember = result;
      this.saveStaffMember();
    },error=>{this.toastr.error("An error occured saving the new person: ", error);});    
  }

  isDisabled(): boolean {
    let isDisabled = this.selectedStaffMember === null || this.selectedStaffMember === undefined;
    isDisabled = isDisabled || (this.selectedStaffMember.id === 0 && this.personDataForm.invalid);
    return isDisabled;
  }

  saveStaffMember(): void {
    this.firmPersonToAdd.person = this.selectedStaffMember;    
    this.firmService.saveFirmPerson(this.firmPersonToAdd).subscribe(result=>{
      this.toastr.success("Successfully saved staff member")
      this.dialogRef.close(true);
    },error=>{
      this.toastr.error("An error occured saving the staff member: ", error);
    });    
  }
}